import { OBJECT_TYPE_MAP, STATUS_MAP } from '@advanza/constants'
import { PreIcon } from '@advanza/ui'
import RequestChatbotBadge from 'components/requests/RequestChatbotBadge'
import RequestEnrichmentMessageTestButton from 'components/requests/RequestEnrichmentMessageTestButton'
import RequestsMoments from 'components/requests/RequestsMoments'
import RequestsMonths from 'components/requests/RequestsMonths'
import DemandUserWhatsappStatusBadge from 'components/ui/DemandUserWhatsappStatusBadge'
import RequestDetailsContainer from 'containers/RequestDetailsContainer'
import { format } from 'date-fns'
import { Request } from 'hooks/requestHooks'
import { RequestEntities, ServiceEntities, User } from 'interfaces/RequestEntities'
import Service from 'interfaces/Services'
import { hasAccess, isSuperUser } from 'misc/user'
import { Fragment, ReactNode } from 'react'
import { Link } from 'react-router-dom'

interface PostalCode {
    place_name?: string
    postal_code?: string
}

interface RequestQuestion {
    question_id: number
    value: string
    bag_info?: {
        construction_date?: string
        status?: string
        object_type?: string
        surface_area?: number
        usages?: string[]
    }
}

interface RequestKeyword {
    keyword: string
}

interface RequestFile {
    id?: number
    name?: string
    url?: string
}

interface ExtendedRequest extends Partial<Request> {
    user_id?: number
    requests_questions?: RequestQuestion[]
    requests_dates?: number[]
    requests_months?: number[]
    requests_moments?: number[]
    requests_keyword?: RequestKeyword
    whatsappValidationStatus?: number | null
    files?: RequestFile[]
    street_name?: string
    house_number?: string
    postal_code?: PostalCode
    contact_preference?: string
    project_time?: string
    request_type?: string
    source?: string
    cta_id?: number
}

interface RequestCardProps {
    request?: ExtendedRequest
    service?: Partial<Service>
    requestEntities?: RequestEntities
    serviceEntities?: ServiceEntities
}

interface TableMapItem {
    key?: keyof ExtendedRequest | keyof User
    name: string
    item?: ReactNode
}

const RequestCard = ({
    request = {},
    service = {},
    requestEntities = {},
    serviceEntities = {},
}: RequestCardProps) => {
    if (!service.questions || !request.requests_options) {
        return null
    }
    const user = request.user_id ? requestEntities.users?.[request.user_id] : undefined
    const whatsappStatus = request.whatsappValidationStatus
    // A consumer that handed its email over to a provider account keeps a prefixed, undeliverable email
    const email = user?.email?.startsWith('$')
        ? user.email_notifications || user.email
        : user?.email
    const phone = user?.phone
    const tableMap: TableMapItem[] = [
        { key: 'first_name', name: 'First name' },
        { key: 'last_name', name: 'Last name' },
        {
            key: 'email',
            name: 'Email',
            item: email ? <a href={`mailto:${email}`}>{email}</a> : null,
        },
        {
            key: 'phone',
            name: 'Phone',
            item: phone ? (
                <span>
                    {phone}
                    <span className="ml-2">
                        <DemandUserWhatsappStatusBadge status={whatsappStatus} />
                    </span>
                </span>
            ) : null,
        },
        {
            name: 'Place',
            item: [
                request.street_name,
                request.house_number,
                request.postal_code?.place_name,
                request.postal_code?.postal_code,
            ]
                .filter(Boolean)
                .join(' '),
        },
        { key: 'subject', name: 'subject' },
        { key: 'contact_preference', name: 'Contact preference' },
        { key: 'project_time', name: 'Time' },
        {
            key: 'request_type',
            name: 'Type',
            item: request.request_type ? (
                <span className="inline-flex items-center gap-2">
                    {request.request_type}
                    <RequestChatbotBadge request={request} />
                </span>
            ) : null,
        },
        {
            key: 'cta_id',
            name: 'Custom CTA',
            item: request.cta_id ? (
                <a
                    style={{ color: '#0077cc' }}
                    target="_blank"
                    rel="noreferrer"
                    href={`/settings/services/service/${request.service_id}`}>
                    {'#' + request.cta_id}
                </a>
            ) : (
                ''
            ),
        },
    ]

    // group requests_dates by question_id
    const dateQuestions =
        request.requests_dates &&
        request.requests_dates.length > 0 &&
        request.requests_dates.reduce<Record<number, string[]>>(
            (allDateQuestions, requestsDateId) => {
                const requestDate = requestEntities.requestsDates?.[requestsDateId]
                if (requestDate) {
                    const { question_id, exec_date } = requestDate
                    return {
                        ...allDateQuestions,
                        [question_id]: [...(allDateQuestions[question_id] ?? []), exec_date],
                    }
                }
                return allDateQuestions
            },
            {},
        )

    const renderListItem = (label: string, value: ReactNode) => {
        if (!value) return null
        return (
            <tr key={label} className="hover:bg-grey-200 transition-colors">
                <td className="py-1.5 pr-4 text-md text-neutral-600 font-medium align-top w-[180px]">
                    {label}
                </td>
                <td className="py-1.5 text-md text-neutral-900 align-top whitespace-break-spaces">
                    {value}
                </td>
            </tr>
        )
    }

    return (
        <div className="py-2">
            <table className="w-full border-collapse">
                <tbody>
                    {tableMap.map(({ key, name, item }) => {
                        let answer: ReactNode = item
                        if (!answer && key) {
                            if (key in request) {
                                answer = request[key as keyof ExtendedRequest] as ReactNode
                            } else if (user && typeof user === 'object' && key in user) {
                                answer = (user as User)[key as keyof User] as ReactNode
                            }
                        }
                        return renderListItem(name, answer)
                    })}
                    {request.details && renderListItem('Details', request.details)}
                    {service.questions.map((id, i) => {
                        const question = serviceEntities.questions?.[id]
                        if (!question) return null

                        const checkedOptions =
                            question.options.length > 0 &&
                            question.options
                                .map((optionId) => {
                                    const requestsOption = request.requests_options?.find(
                                        (item) => item.option_id === optionId,
                                    )
                                    if (!requestsOption) return null
                                    return (
                                        <span
                                            key={optionId}
                                            className="inline-flex items-center gap-1.5">
                                            <span>{requestsOption.value}</span>
                                            {requestsOption.is_pro && (
                                                <Link to={`/sales/leads`}>
                                                    <span className="inline-block px-2 py-0.5 text-sm font-medium bg-neutral-100 text-neutral-700 rounded">
                                                        Is Business
                                                    </span>
                                                </Link>
                                            )}
                                        </span>
                                    )
                                })
                                .filter(Boolean)
                                .reduce<ReactNode[]>((acc, item, index) => {
                                    if (index > 0) {
                                        acc.push(<span key={`sep-${index}`}>, </span>)
                                    }
                                    acc.push(item)
                                    return acc
                                }, [])
                        const extraAnswers: Record<string, string> = {}
                        const answer = request.requests_questions
                            ?.map((questionItem) => {
                                if (questionItem.question_id === id) {
                                    const bagInfo = questionItem.bag_info || {}
                                    if (bagInfo.construction_date) {
                                        extraAnswers['Bouwjaar'] = bagInfo.construction_date
                                    }
                                    if (bagInfo.status) {
                                        extraAnswers['Status'] = (
                                            STATUS_MAP as Record<string, string>
                                        )[bagInfo.status]
                                    }
                                    if (bagInfo.object_type) {
                                        extraAnswers['Type object'] = (
                                            OBJECT_TYPE_MAP as Record<string, string>
                                        )[bagInfo.object_type]
                                    }
                                    if (bagInfo.surface_area) {
                                        extraAnswers['Oppervlakte (m\u00b2)'] = String(
                                            bagInfo.surface_area,
                                        )
                                    }
                                    if (bagInfo.usages && bagInfo.usages.length > 0) {
                                        extraAnswers['Gebruiksdoel'] = bagInfo.usages.join(', ')
                                    }

                                    return (
                                        <div key={id}>
                                            <span>{questionItem.value}</span>
                                        </div>
                                    )
                                }
                                return null
                            })
                            .filter(Boolean)
                        const hasAnswer = Boolean(
                            answer?.length || (checkedOptions && checkedOptions.length),
                        )
                        if (hasAnswer && question.question_name_filter) {
                            return (
                                <Fragment key={i}>
                                    {renderListItem(
                                        question.question_name_filter,
                                        checkedOptions && checkedOptions.length > 0
                                            ? checkedOptions
                                            : answer?.[0],
                                    )}
                                    {Object.keys(extraAnswers).map((key) =>
                                        renderListItem(key, extraAnswers[key]),
                                    )}
                                </Fragment>
                            )
                        }
                        return null
                    })}
                    {dateQuestions &&
                        Object.keys(dateQuestions).map((questionId) => {
                            const questionIdNum = Number(questionId)
                            const question = requestEntities.questions?.[questionIdNum]
                            if (!question) return null

                            return renderListItem(
                                question.question_name_filter,
                                dateQuestions[questionIdNum]
                                    .sort((a, b) => a.localeCompare(b))
                                    .map((date) => format(new Date(date), 'dd MMM yyyy'))
                                    .join(', '),
                            )
                        })}
                    {request.requests_months && request.requests_months.length > 0 && (
                        <RequestsMonths requestsMonthsids={request.requests_months} />
                    )}
                    {request.requests_moments && request.requests_moments.length > 0 && (
                        <RequestsMoments momentIds={request.requests_moments} />
                    )}
                    {request.requests_keyword &&
                        renderListItem('Keyword', request.requests_keyword.keyword)}
                    {request.files &&
                        request.files.length > 0 &&
                        renderListItem(
                            'Files',
                            <PreIcon icon="image">{request.files.length} images</PreIcon>,
                        )}
                </tbody>
            </table>
            {(hasAccess('dev') || isSuperUser()) && request.request_id && (
                <div className="mt-3">
                    <RequestEnrichmentMessageTestButton requestId={request.request_id} />
                </div>
            )}
        </div>
    )
}

export default RequestDetailsContainer(RequestCard)
